<script setup lang="ts">
import { onMounted, ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import {useItineraryStore} from "@/stores/itineraryStore";
import {useFormatStore} from "@/stores/formatStore";
import defaultImage from '@/assets/images/default-itinerary.jpeg';

const { t } = useI18n();

const itineraryStore = useItineraryStore();
const formatStore = useFormatStore();

const route = useRoute();
const router = useRouter();

const pageSize = 9;
const currentPage = ref<number>(Number(route.query.page ?? 1));
const selectedAreaSlug = ref<string | null>(route.query.area as string || null);
const selectedAreaId = computed(() => {
  if (!selectedAreaSlug.value) return '';
  const area = itineraryStore.areas.find(a => a.slug === selectedAreaSlug.value);
  return area?.id || '';
});


const totalPages = computed<number>(() => (itineraryStore.pages ?? 1));
function goToPage(page: number) {
  if (page < 1 || page > totalPages.value) return;
  router.replace({
    query: { ...route.query, page: String(page), area: selectedAreaSlug.value || undefined },
  });
}

onMounted(async () => {
  await itineraryStore.getAllDestinations();
  await itineraryStore.getAll(currentPage.value, pageSize, 'cosmosy', selectedAreaId.value, null, { splitHero: false });
});

watch(
    () => [route.query.page, route.query.area],
    async ([newPage, newSlug]) => {
      currentPage.value = Number(newPage ?? 1);
      selectedAreaSlug.value = newSlug as string || null;

      await itineraryStore.getAll(currentPage.value, pageSize, 'cosmosy', selectedAreaId.value, null, { splitHero: false });

      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
);

function handleAreaSelect(areaSlug: string) {
  selectedAreaSlug.value = areaSlug || null;
  
  const newQuery = { ...route.query, page: '1' };

  if (areaSlug) {
    newQuery.area = areaSlug;
  } else {
    delete newQuery.area; // remove "area" from query completely
  }

  router.replace({ query: newQuery });
}

</script>

<template>
  <div class="container pb-12">
    <h1 class="self-start responsive-title-1 max-md:max-w-full pt-24">
      {{ t('itineraries.title') }}
    </h1>

    <div
        v-if="!itineraryStore.loadingDestinations"
        class="flex flex-wrap gap-4 my-20 max-h-36 overflow-y-auto sm:max-h-full"
    >
      <!-- All Destinations pill -->
      <div
          @click="handleAreaSelect('')"
          :class="[
      'rounded-full p-3 font-semibold cursor-pointer transition-colors',
      selectedAreaSlug === null
        ? 'bg-black text-white'            
        : 'bg-primary-blue text-white hover:bg-black'
    ]"
      >
        All Destinations
      </div>

      <!-- Area pills -->
      <div
          v-for="dest in itineraryStore.areas"
          :key="dest.id"
          @click="handleAreaSelect(dest.slug)"
          :class="[
      'rounded-full p-3 font-semibold cursor-pointer transition-colors',
      selectedAreaSlug === dest.slug
        ? 'bg-black text-white'    
        : 'bg-primary-blue text-white hover:bg-black' 
    ]"
      >
        {{ dest.title }}
      </div>
    </div>
    
    <!-- Loader -->
    <div v-if="itineraryStore.loading" class="flex justify-center items-center min-h-[300px]">
      <div class="w-12 h-12 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
    </div>
    <div v-else-if="!itineraryStore.loading && itineraryStore.itineraries.length === 0" class="container flex flex-col justify-center items-center  py-20 gap-4">
      <img src="@/assets/images/sorry.svg" alt="back" class="2xl:w-18vw xl:w-20vw md:w-30vw sm:w-45vw">
      <div class="font-bold responsive-title-6 text-center">
        {{ t('somethingWentWrong') }}
      </div>
      <div class="text-center">{{ t('search.couldntFindDataDesc') }}</div>
    </div>
    <!-- Itinerary Posts -->
    <div
        v-else
        class="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
    >
      
      <div 
          v-for="itin in itineraryStore.itineraries"
          :key="itin.id"
          class="flex flex-col gap-4 bg-white rounded-lg transition-transform transform md:hover:scale-[1.01]"
      >
        <!-- Image -->
        <router-link :to="`/page/itineraries/${itin.slug}`">
          <img
              :src="itin.imageSet['two-thirds'] || defaultImage"
              :alt="itin.title"
              class="w-full h-[200px] md:h-[240px] object-cover rounded-2xl"
          />
        </router-link>
  
        <!-- Content -->
        <div class=" flex flex-col h-full">
          <div class="flex flex-col gap-2 flex-1">
            <!-- Date -->
            <span v-if="itin.publishedAt" class="text-sm text-gray-500">
              {{
                    formatStore.formatDateWithCustomPatternMapping(
                              itin.publishedAt,
                              "MMM DD, YYYY"
                          )
                        }}
            </span>

            <!-- Title -->
            <router-link
                :to="`/page/itineraries/${itin.slug}`"
                class="text-lg md:text-xl font-semibold line-clamp-2 hover:underline"
            >
              {{ itin.title }}
            </router-link>

            <!-- Multiple area pills -->
            <div v-if="itin.area && itin.area.length" class="flex flex-wrap gap-2">
              <div
                  v-for="ar in itin.area"
                  :key="ar.id"
                  class="bg-primary-blue/10 text-primary-blue rounded-full px-3 py-1 text-xs md:text-sm font-medium"
              >
                {{ ar.title }}
              </div>
            </div>

            <!-- Description -->
            <span class="text-sm md:text-base text-gray-700 line-clamp-3">
              {{ itin.seoDescription }}
            </span>
          </div>


          <!-- Button -->
          <router-link
              :to="`/page/itineraries/${itin.slug}`"
              class="bg-primary-blue w-fit rounded-2xl px-5 py-2 flex shadow-md transition transform hover:scale-105"
          >
            <img
                src="@/assets/icons/svg/Arrow_right_white_16x16.svg"
                alt="arrow"
                class="w-4 h-4"
            />
          </router-link>
        </div>
      </div>
    </div>
    
    <!-- Pagination -->
    <div v-if="totalPages > 1" class="mt-20 flex items-center justify-center gap-2">
      <button
          class="btn px-4 py-1.5 rounded-xl border disabled:opacity-50 shadow-md transition transform hover:scale-105"
          :disabled="currentPage === 1"
          @click="goToPage(currentPage - 1)"
      >
        {{ 'Previous' }}
      </button>

      <button
          v-for="n in totalPages"
          :key="`page-${n}`"
          class="px-3 py-1 rounded-xl border transition hover:shadow"
          :class="n === currentPage ? 'bg-primary-blue text-white' : 'bg-white'"
          @click="goToPage(n)"
      >
        {{ n }}
      </button>

      <button
          class="btn px-3 py-1 rounded-xl border disabled:opacity-50 shadow-md transition transform hover:scale-105"
          :disabled="currentPage === totalPages"
          @click="goToPage(currentPage + 1)"
      >
        {{'Next' }}
      </button>
    </div>
    
  </div>
</template>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.btn {
  background-color: #007bff;
  color: white;
}

</style>